import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { isModuleEnabled, SIDEBAR_TO_MODULE } from '../../lib/modules';
import {
  LayoutDashboard,
  Building2,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  PawPrint,
  UserRound,
  HeartPulse,
  Stethoscope,
  Coins,
  ScrollText,
} from 'lucide-react';

const superAdminNavItems = [
  { label: 'Panel', href: '/superadmin/dashboard', icon: LayoutDashboard },
  { label: 'Klinikler', href: '/superadmin/clinics', icon: Building2 },
  { label: 'Doktorlar', href: '/superadmin/doctors', icon: Stethoscope },
  { label: 'Anneler', href: '/superadmin/mothers', icon: HeartPulse },
  { label: 'Kullanıcılar', href: '/superadmin/users', icon: Users },
  { label: 'Krediler', href: '/superadmin/credits', icon: Coins },
  { label: 'İşlem Geçmişi', href: '/superadmin/audit-log', icon: ScrollText },
  { label: 'Ayarlar', href: '/superadmin/settings', icon: Settings },
];

const doctorNavItems = [
  { label: 'Panel', href: '/doctor/dashboard', icon: LayoutDashboard },
  { label: 'Hastalar', href: '/doctor/patients', icon: UserRound },
  { label: 'Kadin Sagligi', href: '/doctor/womens-health', icon: HeartPulse },
  { label: 'Ayarlar', href: '/doctor/settings', icon: Settings },
];

const creditAdminNavItems = [
  { label: 'Doktor Kredileri', href: '/credit-admin/credits', icon: Coins },
  { label: 'Doktorlar', href: '/credit-admin/doctors', icon: Stethoscope },
];

export const Sidebar = ({ collapsed = false, onToggle }) => {
  const { user, logout, modulesState } = useAuth();
  const location = useLocation();

  const baseNavItems =
    user?.role === 'super_admin'
      ? superAdminNavItems
      : user?.role === 'credit_admin'
        ? creditAdminNavItems
        : doctorNavItems;

  // Filter sidebar items whose href maps to a doctor module. Backwards-compat:
  // items without a module mapping or when the user has no package -> shown as-is.
  const navItems = baseNavItems.filter((item) => {
    const moduleKey = SIDEBAR_TO_MODULE[item.href];
    if (!moduleKey) return true;
    return isModuleEnabled(modulesState, moduleKey);
  });

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-full bg-white border-r border-slate-100 transition-all duration-300 z-40 flex flex-col',
        collapsed ? 'w-16' : 'w-64'
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100 flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#5A67D8] flex items-center justify-center">
              <PawPrint className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              NeoBreed AI
            </span>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-lg bg-[#5A67D8] flex items-center justify-center mx-auto">
            <PawPrint className="w-5 h-5 text-white" />
          </div>
        )}
        {onToggle && !collapsed && (
          <button
            onClick={onToggle}
            className="p-1 rounded-md hover:bg-slate-100 transition-colors"
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="p-3 space-y-1 flex-1">
        {!collapsed && (
          <p className="px-3 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
            Menü
          </p>
        )}
        {navItems.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                isActive
                  ? 'bg-[#5A67D8] text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )}
              data-testid={`sidebar-link-${item.label.toLowerCase()}`}
            >
              <Icon className={cn('w-5 h-5', collapsed && 'mx-auto')} />
              {!collapsed && (
                <span className="font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section: Profile + Logout */}
      <div className="border-t border-slate-100 px-3 pb-4 pt-3 flex-shrink-0">
        {/* Profile Card */}
        {!collapsed ? (
          <Link
            to={user?.role === 'super_admin' ? '/superadmin/settings' : '/doctor/settings'}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50 hover:from-[#5A67D8]/5 hover:to-[#5A67D8]/10 transition-all duration-200 mb-2 group"
            data-testid="sidebar-profile-card"
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 ring-2 ring-white shadow-sm">
              {user?.profile_photo ? (
                <img src={user.profile_photo} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#5A67D8] to-[#4C51BF] flex items-center justify-center">
                  <span className="text-sm font-bold text-white">{getInitials(user?.name)}</span>
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-[#5A67D8] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                {user?.name || 'Kullanıcı'}
              </p>
              <p className="text-[11px] text-slate-400 truncate">{user?.email || ''}</p>
            </div>
          </Link>
        ) : (
          <Link
            to={user?.role === 'super_admin' ? '/superadmin/settings' : '/doctor/settings'}
            className="flex justify-center mb-2"
            data-testid="sidebar-profile-card"
          >
            <div className="w-9 h-9 rounded-xl overflow-hidden ring-2 ring-white shadow-sm">
              {user?.profile_photo ? (
                <img src={user.profile_photo} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#5A67D8] to-[#4C51BF] flex items-center justify-center">
                  <span className="text-xs font-bold text-white">{getInitials(user?.name)}</span>
                </div>
              )}
            </div>
          </Link>
        )}

        {/* Logout */}
        <button
          onClick={logout}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-all duration-200',
            'text-slate-500 hover:bg-red-50 hover:text-red-600'
          )}
          data-testid="sidebar-logout"
        >
          <LogOut className={cn('w-5 h-5', collapsed && 'mx-auto')} />
          {!collapsed && (
            <span className="font-medium text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              Çıkış Yap
            </span>
          )}
        </button>
      </div>
    </aside>
  );
};
